import { gigPermissionAdminApi } from '@/api/admin/gig-permission';
import type { IThunkAPI } from '@/store/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  IGigPermissionRequest,
  IGigPermissionState,
  IPermissionWithLevel,
  IPermissionGroup,
  IGigPermissionAndGroupPost,
  IGigPermissionGroupRequest,
  IGigSummary,
  IGigPermissionPermissionRequest,
} from '../types/types';
import { isFulfilledAction, isPendingAction, isRejectedAction } from '@/helpers';
import type { RootState } from '@/store';

const initialState: IGigPermissionState = {
  gigPermissionList: [],
  gigPermissionGroupList: [],
  gigPermissions: [],
  isError: false,
  isSuccess: false,
  isFetching: false,
};

export const getGigPermissionList = createAsyncThunk<Array<IPermissionWithLevel>, IGigPermissionRequest, IThunkAPI>(
  'get/gig/permission/client/clientId/gig/gigId/list',
  async (requestPayload, thunkAPI) => {
    try {
      const response = await gigPermissionAdminApi.getGigPermission(requestPayload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getGigPermissionGroupList = createAsyncThunk<Array<IPermissionGroup>, IGigPermissionRequest, IThunkAPI>(
  'get/gig/permission/group/client/clientId/gig/gigId/list',
  async (requestPayload, thunkAPI) => {
    try {
      const response = await gigPermissionAdminApi.getGigPermissionGroup(requestPayload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getGigPermissionGroupByClientIdAndGroupIdList = createAsyncThunk<
  Array<IGigSummary>,
  IGigPermissionGroupRequest,
  IThunkAPI
>('get/gig/permission/client/clientId/group/groupId/list', async (requestPayload, thunkAPI) => {
  try {
    const response = await gigPermissionAdminApi.getGigPermissionGroupByClientAndGroupId(requestPayload);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getGigPermissionByClientIdAndPermissionIdList = createAsyncThunk<
  Array<IGigSummary>,
  IGigPermissionPermissionRequest,
  IThunkAPI
>('get/gig/permission/client/clientId/permission/permissionId/list', async (requestPayload, thunkAPI) => {
  try {
    const response = await gigPermissionAdminApi.getGigPermissionGroupByClientAndPermissionId(requestPayload);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const savePermissionsWithGroups = createAsyncThunk<unknown, IGigPermissionAndGroupPost, IThunkAPI>(
  'post/gig/permission/client/clientId/gig/gigId/group/set',
  async (permissions, thunkAPI) => {
    try {
      const response = await gigPermissionAdminApi.savePermissionWithGroup(permissions);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const gigPermissionAdminSlice = createSlice({
  name: 'gigPermissionAdmin',
  initialState,
  reducers: {
    clearGigPermissions(state) {
      state.gigPermissionList = [];
    },
    clearGigPermissionGroups(state) {
      state.gigPermissionGroupList = [];
    },
    clearGigPermissionGroupByClientAndGroupId(state) {
      state.gigPermissions = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getGigPermissionList.fulfilled, (state, action) => {
        state.gigPermissionList = action.payload;
      })
      .addCase(getGigPermissionGroupList.fulfilled, (state, action) => {
        state.gigPermissionGroupList = action.payload;
      })
      .addCase(getGigPermissionGroupByClientIdAndGroupIdList.fulfilled, (state, action) => {
        state.gigPermissions = action.payload;
      })
      .addCase(getGigPermissionByClientIdAndPermissionIdList.fulfilled, (state, action) => {
        state.gigPermissions = action.payload;
      })
      .addMatcher(isFulfilledAction, state => {
        state.isSuccess = true;
        state.isError = false;
        state.isFetching = false;
      })
      .addMatcher(isRejectedAction, state => {
        state.isError = true;
        state.isSuccess = false;
        state.isFetching = false;
      })
      .addMatcher(isPendingAction, state => {
        state.isFetching = true;
      });
  },
});

// actions
export const { clearGigPermissions, clearGigPermissionGroups, clearGigPermissionGroupByClientAndGroupId } =
  gigPermissionAdminSlice.actions;

// selectors
export const gigPermissionList = (state: RootState): IGigPermissionState['gigPermissionList'] =>
  state.gigPermissionAdmin.gigPermissionList;
export const gigPermissionGroupList = (state: RootState): IGigPermissionState['gigPermissionGroupList'] =>
  state.gigPermissionAdmin.gigPermissionGroupList;
export const gigPermissions = (state: RootState): IGigPermissionState['gigPermissions'] =>
  state.gigPermissionAdmin.gigPermissions;

export default gigPermissionAdminSlice.reducer;
